import { useEffect, useRef, useState } from 'react';
import { useParams } from 'wouter';
import { Loader } from '@/components/ui/loader';
import { useToast } from '@/hooks/use-toast';
import { PurchaseDialog } from '@/components/PurchaseDialog';
import { PromoCodeInput } from '@/components/promocodes/PromoCodeInput';
import { useAuth } from '@/hooks/use-auth';
import { useCssVariable } from '@/hooks/use-css-variable';
import { useTitle } from 'react-use';
import { generateTitle } from '@/lib/text.utils';

import { useGameData } from './hooks/use-game-data';
import { useActivePurchase } from './hooks/use-active-purchase';
import { useVideoProgress } from './hooks/use-video-progress';
import { useGameLaunch } from './hooks/use-game-launch';
import { useDiscountedPrice } from './hooks/use-discounted-price';
import { usePurchaseGame } from './hooks/use-purchase-game';

import { GameHero } from './components/game-hero';
import { GameRules } from './components/game-rules';
import { GameRating } from './components/game-rating';
import { RelatedGames } from './components/related-games';
import { GetVideo, type GetVideoHandle } from '@/components/game-attributes/Video';
import type { Promocode } from '@shared/types/schema';
import { useAuthDialog } from '@/context/auth-dialog.context';

export default function Game() {
  const params = useParams<{ id: string }>();
  const id = params.id ? parseInt(params.id, 10) : null;

  const { user } = useAuth();
  const { toast } = useToast();
  useCssVariable('--main-container-max-width', '100%', { removeOnUnmount: true });

  const { game, categories, error: gameError } = useGameData(id);
  const { activePurchase } = useActivePurchase(id, game?.isFree);
  const { progressData } = useVideoProgress(game, false);
  const { signedUrl, isLaunched, showVideo, isLaunching, setShowVideo, launch } =
    useGameLaunch(game);

  const [promocode, setPromocode] = useState<Promocode | undefined>();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [promoCodeInputOpen, setPromoCodeInputOpen] = useState(false);
  const videoRef = useRef<GetVideoHandle>(null);

  const { openDialog: openAuthDialog } = useAuthDialog();

  const purchaseMutation = usePurchaseGame();

  const discountedPrice = useDiscountedPrice(game?.price ?? 0, promocode);
  const priceText = promocode ? (
    <span className="flex items-center gap-2">
      <span className="line-through text-muted-foreground">{game?.price ?? 0} ₽</span>
      <span>{discountedPrice} ₽</span>
    </span>
  ) : (
    `${game?.price ?? 0} ₽`
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.playVideo();
    }
  }, [showVideo]);

  useTitle(generateTitle(game?.title));

  if (!id || isNaN(id)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>Некорректный ID игры</div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (gameError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>Ошибка загрузки игры</div>
      </div>
    );
  }
  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>Игра не найдена</div>
      </div>
    );
  }

  const isGamePurchased = Boolean(activePurchase);

  const handleBuy = () => {
    if (!user) {
      openAuthDialog();
      return;
    }
    setPurchaseDialogOpen(true);
  };

  const handlePromocodeClick = () => {
    if (!user) {
      openAuthDialog();
      return;
    }
    setPromoCodeInputOpen(true);
  };

  const handlePurchaseConfirm = () => {
    purchaseMutation.mutate(
      {
        gameId: id!,
        promocodeCode: promocode?.code,
      },
      {
        onSuccess: () => {
          setPurchaseDialogOpen(false);
        },
        onError: (error) => {
          toast({
            title: 'Ошибка покупки',
            description: error.message,
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <>
      <GameHero
        game={game}
        isGamePurchased={isGamePurchased}
        isLaunching={isLaunching}
        categories={categories}
        handleBuy={handleBuy}
        handlePromocodeClick={handlePromocodeClick}
        handleGameLaunch={launch}
        priceText={priceText}
      />

      <div className="main-container !mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mb-14">
          <GameRules game={game} />
          <GameRating gameId={id} />
        </div>
        <RelatedGames gameId={id} />

        {showVideo && signedUrl && (
          <div className="mt-8">
            <GetVideo
              ref={videoRef}
              signedUrl={signedUrl}
              game={game}
              isGameLaunched={isLaunched}
              isGamePurchased={isGamePurchased}
              setShowVideo={setShowVideo}
              initialTime={progressData?.progressSeconds}
            />
          </div>
        )}
      </div>

      <PurchaseDialog
        open={purchaseDialogOpen}
        onOpenChange={setPurchaseDialogOpen}
        onConfirm={handlePurchaseConfirm}
        price={game.price ?? 0}
        loading={purchaseMutation.isPending}
        promocode={promocode}
      />

      <PromoCodeInput
        open={promoCodeInputOpen}
        onOpenChange={setPromoCodeInputOpen}
        onApply={setPromocode}
      />
    </>
  );
}
